/**
 * Move-stage dialog. Enforces company rule R3: a reason is required in the UI
 * (preset dropdown + optional note), even though the API accepts an empty reason.
 * Moving to the current stage is disallowed (the API rejects a no-op with 400).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { FloatingDropdown } from '../../../components/ui/FloatingDropdown';
import { Textarea } from '../../../components/ui/Input';
import { cn } from '../../../utils/cn';
import type { Stage } from '../../../services/recruitmentApi';
import { STAGES, STAGE_META, REASON_PRESETS, REASON_OTHER } from '../config/constants';
import { StageBadge } from './badges';
import { InlineError } from './states';

export interface MoveStageDialogProps {
	open: boolean;
	onClose: () => void;
	currentStage: Stage;
	/** Pre-selected target (e.g. the column a card was dragged into). Still editable. */
	initialTo?: Stage;
	onConfirm: (to: Stage, reason: string) => void;
	loading?: boolean;
	error?: string | null;
	candidateName?: string;
}

export const MoveStageDialog: React.FC<MoveStageDialogProps> = ({
	open,
	onClose,
	currentStage,
	initialTo,
	onConfirm,
	loading = false,
	error,
	candidateName,
}) => {
	const [to, setTo] = useState<string>('');
	const [reason, setReason] = useState<string>('');
	const [touched, setTouched] = useState(false);

	// Reset each time the dialog opens.
	useEffect(() => {
		if (open) {
			setTo(initialTo && initialTo !== currentStage ? initialTo : '');
			setReason('');
			setTouched(false);
		}
	}, [open, initialTo, currentStage]);

	const stageOptions = useMemo(
		() =>
			STAGES.map(s => ({
				value: s,
				label: STAGE_META[s].label,
				disabled: s === currentStage, // can't move to the same stage
			})),
		[currentStage]
	);

	// Preset reasons are quick-fills for the free-text box (the real input); the
	// 'Other…' sentinel is dropped since typing your own is always available.
	const quickReasons = useMemo(() => REASON_PRESETS.filter(r => r !== REASON_OTHER), []);

	const stageValid = !!to && to !== currentStage;
	const reasonValid = reason.trim().length > 0;
	const canSubmit = stageValid && reasonValid && !loading;

	const handleConfirm = () => {
		setTouched(true);
		if (!canSubmit) return;
		onConfirm(to as Stage, reason.trim());
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			title='Move stage'
			size='md'
			footer={
				<div className='flex justify-end gap-2'>
					<Button variant='outline' onClick={onClose} disabled={loading}>
						Cancel
					</Button>
					<Button variant='primary' onClick={handleConfirm} loading={loading} disabled={!canSubmit}>
						Confirm move
					</Button>
				</div>
			}
		>
			<div className='space-y-4'>
				<div className='flex items-center gap-2 text-sm text-gray-600'>
					<span>Currently</span>
					<StageBadge stage={currentStage} />
					{candidateName && (
						<span className='truncate'>
							· <span className='font-medium text-gray-900'>{candidateName}</span>
						</span>
					)}
				</div>

				<FloatingDropdown
					label='Move to stage'
					required
					options={stageOptions}
					value={to}
					onChange={setTo}
					searchable={false}
					error={touched && !stageValid}
					errorMessage='Pick a different stage to move to.'
				/>

				<div>
					<label className='mb-1.5 block text-sm font-medium text-gray-700'>
						Reason <span className='text-red-500'>*</span>
					</label>
					{/* Quick-fill chips — tapping one drops it into the box below, which stays editable. */}
					<div className='mb-2 flex flex-wrap gap-2'>
						{quickReasons.map(r => (
							<button
								key={r}
								type='button'
								onClick={() => setReason(r)}
								className={cn(
									'rounded-full border px-2.5 py-1 text-xs font-medium transition',
									reason.trim() === r
										? 'border-primary bg-primary-50 text-primary-700'
										: 'border-gray-200 bg-white text-gray-600 hover:border-primary/40 hover:text-gray-900'
								)}
							>
								{r}
							</button>
						))}
					</div>
					<Textarea
						value={reason}
						onChange={e => setReason(e.target.value)}
						rows={3}
						maxLength={500}
						placeholder='Pick a quick reason above, or type your own…'
						error={touched && !reasonValid ? 'A reason is required for every stage move.' : undefined}
					/>
				</div>

				{error && <InlineError message={error} />}

				<p className='text-xs text-gray-400'>
					This move is recorded in the stage history with who, when and why — it cannot be edited later.
				</p>
			</div>
		</Modal>
	);
};
